import { Card } from '@/components/ui/card';
import { Leaf, Target, Shield, BarChart3, Globe, Zap, Users, Award } from 'lucide-react';

const features = [
  { icon: Target, title: 'Product Carbon Footprint', desc: 'Calculate precise PCF for every product using verified emission factors and real production data.' },
  { icon: Shield, title: 'CBAM Compliance', desc: 'Stay ahead of EU Carbon Border Adjustment Mechanism requirements with automated reporting.' },
  { icon: BarChart3, title: 'Unified Accounting', desc: 'Reconcile factory-level (CCF) and product-level (PCF) carbon accounting in a single view.' },
  { icon: Globe, title: 'Supply Chain Tracking', desc: 'Monitor supplier emissions, data quality, and decarbonization progress across your value chain.' },
  { icon: Zap, title: 'Living PCF', desc: 'Track real-time PCF evolution across all products with monthly trend analysis.' },
  { icon: Users, title: 'Role-Based Dashboards', desc: 'Tailored views for CFO (financial), CPO (procurement), and CSO (sustainability) decision-makers.' },
];

const stats = [
  { value: '50+', label: 'Emission Factors' },
  { value: '6', label: 'CBAM Sectors' },
  { value: '99%', label: 'Uptime SLA' },
  { value: '7', label: 'Languages' },
];

export default function About() {
  return (
    <div data-testid="about-page" className="space-y-10 max-w-5xl mx-auto">
      {/* Hero */}
      <section className="text-center space-y-4 animate-fade-in" data-testid="about-hero">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-2">
          <Leaf className="w-8 h-8 text-primary" />
        </div>
        <h1 className="text-3xl md:text-4xl font-heading font-bold tracking-tight">About Veridex</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
          The next-generation carbon accounting platform built for manufacturers navigating CBAM compliance.
          We combine precision engineering with intelligent automation to deliver accurate, auditable product carbon footprints.
        </p>
      </section>

      {/* Stats Bar */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="about-stats">
        {stats.map((s, i) => (
          <Card key={s.label} className={`p-5 text-center animate-fade-in stagger-${i + 1}`}>
            <p className="text-3xl font-heading font-bold text-primary">{s.value}</p>
            <p className="text-sm text-muted-foreground mt-1">{s.label}</p>
          </Card>
        ))}
      </section>

      {/* Mission */}
      <section className="animate-fade-in" data-testid="about-mission">
        <Card className="p-6 md:p-8 border-primary/20 bg-primary/[0.02]">
          <h2 className="text-xl font-heading font-bold mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-primary" /> Our Mission
          </h2>
          <p className="text-muted-foreground leading-relaxed">
            Veridex exists to make carbon accounting accessible, accurate, and actionable for every manufacturer.
            As CBAM reshapes global trade, we provide the tools to measure, report, and reduce product-level emissions
            with confidence. Our platform bridges the gap between complex regulatory requirements and practical business operations,
            enabling companies to turn compliance into a competitive advantage.
          </p>
        </Card>
      </section>

      {/* Features Grid */}
      <section data-testid="about-features">
        <h2 className="text-xl font-heading font-bold mb-5 text-center">Platform Capabilities</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {features.map((f, i) => {
            const Icon = f.icon;
            return (
              <Card key={f.title} className={`p-5 transition-all duration-300 hover:shadow-lg hover:-translate-y-0.5 animate-fade-in stagger-${(i % 4) + 1}`} data-testid={`feature-${i}`}>
                <div className="w-10 h-10 rounded-lg bg-accent/10 flex items-center justify-center mb-3">
                  <Icon className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-heading font-semibold text-sm mb-1.5">{f.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{f.desc}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Tech Stack */}
      <section className="animate-fade-in" data-testid="about-tech">
        <Card className="p-6">
          <h2 className="text-xl font-heading font-bold mb-4">Technology Stack</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            {[
              { label: 'Frontend', items: ['React 19', 'Tailwind CSS', 'Recharts', 'shadcn/ui'] },
              { label: 'Backend', items: ['FastAPI', 'Python 3.12', 'Motor (Async)', 'Pydantic'] },
              { label: 'Database', items: ['MongoDB', 'Async Driver', 'Auto-indexing', 'Aggregation'] },
              { label: 'Features', items: ['Dark Mode', 'Responsive', 'Real-time Charts', 'PDF Export'] },
            ].map(group => (
              <div key={group.label}>
                <p className="font-heading font-semibold text-primary mb-2">{group.label}</p>
                <ul className="space-y-1 text-muted-foreground text-xs">
                  {group.items.map(item => <li key={item} className="flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-primary/50" />{item}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </Card>
      </section>

      {/* Footer */}
      <section className="text-center text-xs text-muted-foreground pb-4 animate-fade-in" data-testid="about-footer">
        <p>Veridex One &copy; 2025. Built for CBAM compliance.</p>
      </section>
    </div>
  );
}
